import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Inbox } from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { Activity } from '@/types'
import { getActivityIcon, getActivityIconColor, getActivityRoute } from './utils'

interface ActivityFeedCardProps {
  activities: Activity[]
  loading: boolean
  error?: string | null
}

/**
 * ActivityItem - Individual activity entry in the feed
 */
interface ActivityItemProps {
  activity: Activity
}

function ActivityItem({ activity }: ActivityItemProps) {
  const navigate = useNavigate()
  const Icon = getActivityIcon(activity.type)
  const iconColor = getActivityIconColor(activity.type)

  const handleClick = () => {
    const route = getActivityRoute(activity.type, activity.entityId)
    navigate({ to: route })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  // Format relative time in Portuguese
  const formatRelativeTime = (timestamp: string): string => {
    try {
      return formatDistanceToNow(parseISO(timestamp), {
        addSuffix: true,
        locale: ptBR
      })
    } catch {
      return timestamp
    }
  }

  // Format full date for tooltip
  const formatFullDate = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return timestamp
    }
  }

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className="flex items-start gap-3 p-3 rounded-monumenta hover:bg-brand-dark-lighter cursor-pointer transition-all duration-200"
    >
      <div className="flex-shrink-0 mt-1">
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-body">
          {activity.description}
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-brand-gray">
          <span>{activity.actor}</span>
          <span>•</span>
          <span title={formatFullDate(activity.timestamp)}>
            {formatRelativeTime(activity.timestamp)}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * ActivityFeedCard - Main component displaying recent RevFinOps activities
 *
 * Shows last 10 activities from clients, payables, and assignments
 * Includes loading states, error handling, and empty state
 */
export function ActivityFeedCard({ activities, loading, error }: ActivityFeedCardProps) {
  // Loading state
  if (loading) {
    return (
      <div className="card mt-6">
        <h2 className="text-xl font-display font-semibold text-white mb-4">
          Atividades Recentes
        </h2>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="loading-pulse h-16 rounded-monumenta" />
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="card mt-6 border-brand-red/30">
        <h2 className="text-xl font-display font-semibold text-white mb-4">
          Atividades Recentes
        </h2>
        <div className="flex items-center gap-2 text-brand-red">
          <p className="text-sm font-body">{error}</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (!activities || activities.length === 0) {
    return (
      <div className="card mt-6">
        <h2 className="text-xl font-display font-semibold text-white mb-4">
          Atividades Recentes
        </h2>
        <div className="flex flex-col items-center justify-center py-12">
          <Inbox className="h-12 w-12 text-brand-gray opacity-50 mb-3" />
          <p className="text-brand-gray-light font-body">Nenhuma atividade recente</p>
        </div>
      </div>
    )
  }

  // Normal state with activities
  return (
    <div className="card mt-6">
      <h2 className="text-xl font-display font-semibold text-white mb-4">
        Atividades Recentes
      </h2>
      <div className="space-y-2">
        {activities.map(activity => (
          <ActivityItem key={activity.id} activity={activity} />
        ))}
      </div>
    </div>
  )
}
